import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    // Use plain axios to avoid the global 401 interceptor which would force a full-page
    // redirect to /login on unauthorized responses. We want the login form to handle
    // invalid credentials inline without reloading.
    return axios.post(`${API_URL}/auth/login`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  register: (userData) => 
    api.post('/auth/register', userData),
  getCurrentUser: () => 
    api.get('/auth/me'),
  // Admin endpoints
  getAllUsers: () =>
    api.get('/auth/users'),
  getPendingRegistrations: () =>
    api.get('/auth/pending'),
  approveUser: (userId) =>
    api.patch(`/auth/approve/${userId}`),
  updateUser: (userId, updates) =>
    api.patch(`/auth/admin/users/${userId}`, updates),
  deleteUser: (userId) =>
    api.delete(`/auth/admin/users/${userId}`),
  changeUserPassword: (userId, newPassword) =>
    api.post(`/auth/admin/change-password/${userId}`, { new_password: newPassword }),
  changeOwnPassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// Translations API
export const translationsAPI = {
  getById: (id) => 
    api.get(`/translations/${id}`),
  getPair: (id) =>
    api.get(`/translations/pair/${id}`),
  getContext: (id, before = 2, after = 2) =>
    api.get(`/translations/context/${id}?before=${before}&after=${after}`),
  getNext: () => 
    api.get('/translations/next'),
  getNextSuitable: () =>
    api.get('/translations/next/suitable'),
  getNextFrom: (currentId) => 
    api.get(`/translations/navigation/next/${currentId}`),
  getPrevious: (currentId) => 
    api.get(`/translations/navigation/previous/${currentId}`),
  getRandom: () => 
    axios.get(`${API_URL}/translations/random`), // Sin autenticación
  getStats: () => 
    api.get('/translations/stats'),
  getPublicStats: () => 
    axios.get(`${API_URL}/translations/stats/public`), // Sin autenticación
  getReport: () =>
    api.get('/translations/report/summary'),
  getMyValidatedHistory: (skip = 0, limit = 20) => 
    api.get(`/translations/validated/my-history?skip=${skip}&limit=${limit}`),
  getMyPendingTranslations: (skip = 0, limit = 20) => 
    api.get(`/translations/pending/for-me?skip=${skip}&limit=${limit}`),
  getAdminList: (skip = 0, limit = 20, q = '') =>
    api.get(`/translations/admin/list?skip=${skip}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  updateDirection: (translationId, direction) =>
    api.patch(`/translations/${translationId}/direction`, { direction }),
  adminCreate: (data) =>
    api.post('/translations/admin/create', data),
};

// Validations API
export const validationsAPI = {
  create: (data) => 
    api.post('/validations', data),
  update: (id, data) => 
    api.put(`/validations/${id}`, data),
  getMyHistory: (skip = 0, limit = 50) => 
    api.get(`/validations/my-history?skip=${skip}&limit=${limit}`),
  getByTranslation: (translationId) => 
    api.get(`/validations/by-translation/${translationId}`),
  getAllByTranslation: (translationId) =>
    api.get(`/validations/admin/by-translation/${translationId}`),
};

// Suggestions API
export const suggestionsAPI = {
  create: (data) => 
    api.post('/suggestions', data),
  update: (id, data) =>
    api.put(`/suggestions/${id}`, data),
  getByTranslation: (translationId) => 
    api.get(`/suggestions?translation_id=${translationId}`),
  getMySuggestions: (skip = 0, limit = 50) => 
    api.get(`/suggestions/my-suggestions?skip=${skip}&limit=${limit}`),
  getStats: () => 
    api.get('/suggestions/stats'),
  delete: (suggestionId) => 
    api.delete(`/suggestions/${suggestionId}`),
};

// Validator Groups API (admin)
export const validatorGroupsAPI = {
  list: () =>
    api.get('/validator-groups'),
  create: (data) =>
    api.post('/validator-groups', data),
  delete: (id) =>
    api.delete(`/validator-groups/${id}`),
  mySupervised: () =>
    api.get('/validator-groups/my-supervised'),
};

// Supervisor API
export const supervisorAPI = {
  getStudentsValidations: (params) =>
    api.get('/supervisor/students-validations', { params }),
  getStudentsSuggestions: (params) =>
    api.get('/supervisor/students-suggestions', { params }),
  getProdigyAnnotations: (params) =>
    api.get('/supervisor/prodigy-annotations', { params }),
  getProdigyProgress: () =>
    api.get('/supervisor/prodigy-progress'),
  submitReview: (data) =>
    api.post('/supervisor/review', data),
  // For students: fetch reviews their supervisor(s) left on their work
  getMyReviews: () =>
    api.get('/supervisor/my-reviews'),
  // For students: fetch returned items that need correction
  getMyReturned: () =>
    api.get('/supervisor/my-returned'),
  // Re-annotation (student)
  getReannotationPending: () =>
    api.get('/supervisor/reannotation-pending'),
  createReannotationBatch: (annotationType) =>
    api.post(`/supervisor/create-reannotation-batch?annotation_type=${encodeURIComponent(annotationType)}`),
};

// Notifications API
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Prodigy Groups & Config API (NC-68)
export const prodigyGroupsAPI = {
  getGroups: () => api.get('/prodigy/groups'),
  createGroup: (data) => api.post('/prodigy/groups', data),
  updateGroup: (id, data) => api.put(`/prodigy/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/prodigy/groups/${id}`),
  addMember: (groupId, data) => api.post(`/prodigy/groups/${groupId}/members`, data),
  removeMember: (groupId, userId) => api.delete(`/prodigy/groups/${groupId}/members/${userId}`),
  updateBatch: (groupId, data) =>
    api.put(`/prodigy/groups/${groupId}/batch`, typeof data === 'number' ? { current_batch: data } : data),
  startService: (groupId) => api.post(`/prodigy/groups/${groupId}/start-service`),
  stopService: (groupId) => api.post(`/prodigy/groups/${groupId}/stop-service`),
  startAllBatches: (groupId) => api.post(`/prodigy/groups/${groupId}/start-all-batches`),
  stopAllBatches: (groupId) => api.post(`/prodigy/groups/${groupId}/stop-all-batches`),
  getMyBatches: () => api.get('/prodigy/my-batches'),
  startBatchInstance: (serviceId) => api.post(`/prodigy/start-batch-instance/${serviceId}`),
  getConfig: () => api.get('/prodigy/config'),
  updateConfig: (key, value) => api.put(`/prodigy/config/${key}`, { value }),
  getStatus: () => api.get('/prodigy/status'),
  getInstances: () => api.get('/prodigy/instances'),
  stopInstance: (serviceId) => api.delete(`/prodigy/instances/${serviceId}`),
};

export default api;